// Bot AI, ported from the prototype's botThink. Bots use every player ability: move, split, boost,
// eject (to shoot viruses), power-ups. Skill (from difficulty + skin price) decides how often and how well.
// The World calls botThink on each bot's think tick and acts on the result after a reaction delay.
import { MAX_PRICE, SKIN_BY_ID, WORLD, rad } from './config';
import type { Difficulty, PowerUpType, SkinId } from './config';
import type { BotBrain, Cell, Pellet, Player, PowerUp, Virus } from './types';
import { randIn, type Rng } from './rng';

export type BotSkill = BotBrain['skill'];

// What a bot can see. World satisfies this structurally, so this file never imports sim.ts.
export interface BotWorld {
  readonly now: number;
  readonly players: readonly Player[];
  readonly pellets: readonly Pellet[];
  readonly viruses: readonly Virus[];
  readonly powerups: readonly PowerUp[];
  readonly rng: Rng;
  has(p: Player, k: PowerUpType): boolean;
}

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
const dist = (dx: number, dy: number) => Math.sqrt(dx * dx + dy * dy);

// ========== cell helpers (shared with sim.ts, which re-exports them) ==========
export function totalMass(p: Player): number {
  let m = 0;
  for (const c of p.cells) m += c.m;
  return m;
}
// mass-weighted centre; a player with no cells reports its steering target
export function centerOf(p: Player): { x: number; y: number } {
  let x = 0, y = 0, m = 0;
  for (const c of p.cells) { x += c.x * c.m; y += c.y * c.m; m += c.m; }
  return m ? { x: x / m, y: y / m } : { x: p.tx, y: p.ty };
}
export function biggestCell(p: Player): Cell | null {
  let big: Cell | null = null;
  for (const c of p.cells) if (!big || c.m > big.m) big = c;
  return big;
}

// Golden Blob: fixed skill, never splits at anyone (it runs, players hunt it)
export const GOLDEN_SKILL: BotSkill = { think: 0.2, react: 0.18, noise: 30, agg: 0, aware: 1, view: 1.2 };

// tier 0 (free skin) .. 1 (most expensive): fancier skins get faster reactions, better aim, more aggression and awareness
export function botSkill(diff: Difficulty, skinId: SkinId, rng: Rng): BotSkill {
  const t = Math.min(1, (SKIN_BY_ID[skinId]?.price ?? 0) / MAX_PRICE || 0);
  const k = Math.sqrt(t);
  return {
    think: diff.think * (1.25 - 0.6 * k),
    react: diff.react * (1.3 - 0.6 * k),
    noise: diff.noise * (1.4 - 1.0 * k) * randIn(rng, 0.8, 1.2),
    agg: Math.min(0.9, diff.agg * (0.6 + 1.6 * k) * randIn(rng, 0.8, 1.2)),
    aware: Math.min(0.99, diff.aware + (1 - diff.aware) * k),
    view: Math.min(1.1, diff.view + 0.2 * k),
  };
}

export function newBrain(): BotBrain {
  return {
    skill: { think: 0.3, react: 0.3, noise: 60, agg: 0.2, aware: 0.85, view: 0.9 },
    nextThink: 0, queue: [], gx: 0, gy: 0, ax: 0, ay: 0, wander: null,
    wantSplit: false, wantBoost: false, wantEject: false, boost: false,
  };
}

// Decides where bot b wants to go (gx, gy) and whether to split, boost or eject on the way.
export function botThink(w: BotWorld, b: Player): void {
  const brain = b.bot;
  if (!brain) return;
  const sk = brain.skill, now = w.now, rng = w.rng;
  brain.nextThink = now + sk.think * randIn(rng, 0.8, 1.3);
  brain.wantBoost = false; brain.wantEject = false;
  const big = biggestCell(b);
  if (!big) return;
  const ctr = centerOf(b);
  const bigR = rad(big.m);
  const mass = totalMass(b);
  const view = (550 + bigR * 4) * sk.view;
  const shielded = w.has(b, 'shield'), ghost = w.has(b, 'ghost');
  const skill01 = clamp(1 - sk.think / 0.55, 0, 1); // ~0 clumsy .. ~1 sharp
  let fx = 0, fy = 0, threat = false, threatCell: Cell | null = null, threatDist = Infinity;
  let prey: Cell | null = null, preyScore = 0, preyDist = 0;

  for (const p of w.players) {
    if (p === b || !p.alive) continue;
    for (const c of p.cells) {
      const dx = c.x - ctr.x, dy = c.y - ctr.y, d = dist(dx, dy);
      if (d > view) continue;
      const cr = rad(c.m);
      if (c.m > big.m * 1.25) {
        if (shielded) continue; // can't be eaten right now: ignore threats
        // bigger danger radius if it could split onto us
        const reach = cr + bigR + (c.m / 2 > big.m * 1.25 ? 350 : 120);
        if (d < reach && rng() < sk.aware) {
          const k = (reach - d) / reach + 0.2;
          fx -= dx / (d || 1) * k; fy -= dy / (d || 1) * k;
          threat = true;
          if (d < threatDist) { threatDist = d; threatCell = c; }
        }
      } else if (big.m > c.m * 1.25 && !w.has(p, 'shield')) {
        const s = c.m / (d + 80) * (p.golden ? 4 : 1);
        if (s > preyScore) { preyScore = s; prey = c; preyDist = d; }
      }
    }
  }
  // big bots steer around viruses (not while ghosted)
  if (big.m > 133 && !ghost) for (const v of w.viruses) {
    const dx = v.x - ctr.x, dy = v.y - ctr.y, d = dist(dx, dy) || 1;
    if (d < bigR + 120) { fx -= dx / d * 0.6; fy -= dy / d * 0.6; }
  }

  // virus shot: a virus sits between us and a big enemy -> feed it so it fires at them
  if (mass > 80 && rng() < sk.agg * 1.5) {
    const enemy = threatCell || (prey && prey.m > 133 ? prey : null);
    if (enemy && enemy.m > 133) for (const v of w.viruses) {
      const vd = dist(v.x - ctr.x, v.y - ctr.y);
      if (vd > bigR + 350 || vd < bigR + 20) continue;
      const ax = (v.x - ctr.x) / vd, ay = (v.y - ctr.y) / vd;
      const ex = enemy.x - v.x, ey = enemy.y - v.y, ed = dist(ex, ey);
      if (ed < 600 && (ax * ex + ay * ey) / ed > 0.92) {
        brain.gx = v.x; brain.gy = v.y; brain.wantEject = true;
        return;
      }
    }
  }

  if (threat && threatCell) {
    // flee, push away from walls too; boost if they're close
    if (ctr.x < 300) fx += 0.8;
    if (ctr.x > WORLD - 300) fx -= 0.8;
    if (ctr.y < 300) fy += 0.8;
    if (ctr.y > WORLD - 300) fy -= 0.8;
    const l = dist(fx, fy) || 1;
    brain.gx = ctr.x + fx / l * 600; brain.gy = ctr.y + fy / l * 600;
    const tr = rad(threatCell.m);
    brain.wantBoost = mass > 50 && threatDist < tr + bigR + 200 && rng() < 0.3 + 0.6 * skill01;
    // desperate escape: eject mass to get lighter and faster
    if (threatDist < tr + bigR * 0.5 && mass > 60 && rng() < sk.agg) brain.wantEject = true;
    return;
  }

  // power-ups are worth a detour
  let pu: PowerUp | null = null, pud = Infinity;
  for (const o of w.powerups) {
    const d = dist(o.x - ctr.x, o.y - ctr.y);
    if (d < view * 0.8 && d < pud) { pud = d; pu = o; }
  }
  if (pu && (!prey || pud < preyDist * 0.7)) {
    brain.gx = pu.x; brain.gy = pu.y;
    brain.wantBoost = mass > 80 && pud > 300 && rng() < 0.4 * skill01;
    return;
  }

  if (prey) {
    brain.gx = prey.x + randIn(rng, -sk.noise, sk.noise) + fx * 100;
    brain.gy = prey.y + randIn(rng, -sk.noise, sk.noise) + fy * 100;
    const canSplitKill = b.cells.length <= 2 && big.m / 2 > prey.m * 1.3 && preyDist < bigR + 330 && preyDist > bigR * 0.7;
    if (canSplitKill && rng() < sk.agg) brain.wantSplit = true;
    // close the gap with a short boost when the prey is just out of reach
    else if (mass > 70 && preyDist < bigR + 300 && preyDist > bigR && rng() < sk.agg * 1.2 + (prey.owner.golden ? 0.4 : 0)) brain.wantBoost = true;
    return;
  }

  // forage: head for the nearest of 80 sampled pellets (boost dots count, rush food is worth more), otherwise wander
  const ps = w.pellets;
  let best: Pellet | null = null, bd = Infinity;
  for (let i = 0; i < 80; i++) {
    const f = ps[(rng() * ps.length) | 0];
    if (!f || f.dead) continue;
    const d = dist(f.x - ctr.x, f.y - ctr.y) * (f.kind === 2 ? 0.5 : 1);
    if (d < bd) { bd = d; best = f; }
  }
  if (best && bd < view) { brain.gx = best.x + fx * 100; brain.gy = best.y + fy * 100; }
  else {
    if (!brain.wander || dist(brain.wander.x - ctr.x, brain.wander.y - ctr.y) < 100) {
      brain.wander = { x: randIn(rng, 200, WORLD - 200), y: randIn(rng, 200, WORLD - 200) };
    }
    brain.gx = brain.wander.x; brain.gy = brain.wander.y;
  }
}
